/*eslint max-lines-per-function: ["error", 217]*/
import React, { useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useEffect } from 'react';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../translations';
import {
  CreateInvoiceToContractorEmailDto,
  CreateUpdateAttachmentDtoEnObjectTypeEnum,
  GetEmailAddressDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import FormRow from '../../../app/components/FormRow';
import DictionarySelector from '../Selects/DictionarySelector';
import { validate } from '../../../common/pokCore/contexts/EmailContext';
import ValidationAlert from '../../../app/components/ValidationAlert';
import Button from '../../../common/components/Button/Button';
import DragNDropFiles, {
  DragNDropFile,
  NewFile,
} from '../../../common/components/File/DragNDropFiles';
import { convertUploadedFilesToAttachments } from '../../../common/components/File/DragNDropUtils';

export const EmailResendForm = (props: {
  invoiceIds: string[];
  emailId: string;
  emailAddresses: GetEmailAddressDto | undefined;
  show: boolean;
  duplicate: boolean;
  existingFiles?: DragNDropFile[];
  refreshData: () => void;
  onClose: () => void;
}) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('fvsLng');

  const [sending, setSending] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [filesToAdd, setFilesToAdd] = useState<NewFile[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [maxFileSizeExceeded, setMaxFileSizeExceeded] = useState(false);
  const [emailToContractor, setEmailToContractor] =
    useState<CreateInvoiceToContractorEmailDto>({
      emailFromId: props.emailAddresses?.emailFrom.id || '',
      emailTo: props.emailAddresses?.emailsTo || '',
      emailDw: props.emailAddresses?.emailsDw || '',
      subject: '',
      message: '',
      duplicate: props.duplicate,
      invoiceIds: props.invoiceIds,
    });

  useEffect(() => {
    let emailFromId = props.emailAddresses?.emailFrom.id;
    pok.emails
      .getById(props.emailId, true)
      .then(emailData => {
        if (!emailFromId) {
          return pok.dictionaries
            .getByDictionaryType(
              'Adresy nadawcze dla faktur skonfigurowane w Gmail',
            )
            .then(emailFromOptions => {
              emailFromId = emailFromOptions.find(
                option => option.value === emailData.sentFrom,
              )?.id;
              return { emailData, emailFromId };
            });
        }
        return { emailData, emailFromId };
      })
      .then(({ emailData, emailFromId }) => {
        setEmailToContractor({
          ...emailToContractor,
          emailFromId: emailFromId,
          emailTo: props.emailAddresses?.emailsTo || emailData.sentTo,
          emailDw: props.emailAddresses?.emailsDw || emailData.sentCc,
          subject: emailData.subject || '',
          message: emailData.message || '',
        });
      })
      .catch(error => {
        notifications.caughtError(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, pok.emails, props.emailAddresses, props.emailId]);

  useEffect(() => {
    if (!props.show) {
      setShowErrors(false);
      setErrors([]);
      setSending(false);
    }
  }, [props.show]);

  const handleSend = async () => {
    const status = await validate(emailToContractor);
    if (!status.valid) {
      setShowErrors(true);
      setErrors(status.errors);
      return;
    }
    if (maxFileSizeExceeded) {
      setShowErrors(true);
      setErrors([t(tk.attachment.fileSizeExceeded)]);
      return;
    }
    setSending(true);
    if (filesToAdd.length > 0 || filesToDelete.length > 0) {
      const attachments = convertUploadedFilesToAttachments(
        filesToAdd,
        filesToDelete,
        props.invoiceIds[0],
        CreateUpdateAttachmentDtoEnObjectTypeEnum.SalesInvoice,
      );

      await pok.attachments
        .addDeleteAttachments(attachments)
        .then(() => {
          setFilesToAdd([]);
          setFilesToDelete([]);
        })
        .catch(async errorResponse => {
          setShowErrors(true);
          setErrors(await responseErrors(errorResponse as ResponseError));
          return;
        });
    }
    pok.emails
      .sendInvoiceToContractorFromParams(emailToContractor)
      .then(() => {
        setSending(false);
        props.refreshData();
        props.onClose();
      })
      .catch(async errorResponse => {
        setSending(false);
        setShowErrors(true);
        setErrors(await responseErrors(errorResponse as ResponseError));
      });
  };

  const propertyChange = (obj: Partial<CreateInvoiceToContractorEmailDto>) => {
    setEmailToContractor(prev => ({ ...prev, ...obj }));
  };

  return (
    <Modal
      show={props.show}
      onHide={props.onClose}
      backdrop="static"
      centered
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.duplicate
            ? t(tk.sendEmail.sendAgainFVSDuplicate)
            : t(tk.sendEmail.sendAgainFVS)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="d-grid gap-4 mb-3">
          <FormRow controlId="emailFrom" label={t(tk.sendEmail.fromAddress)}>
            <DictionarySelector
              dictionary="Adresy nadawcze dla faktur skonfigurowane w Gmail"
              value={emailToContractor?.emailFromId}
              onChange={option => propertyChange({ emailFromId: option.value })}
            />
          </FormRow>
          <FormRow controlId="emailTo" label={t(tk.sendEmail.toAddress)}>
            <Form.Control
              type="email"
              value={emailToContractor?.emailTo}
              onChange={e => propertyChange({ emailTo: e.target.value })}
              required
            />
          </FormRow>
          <FormRow controlId="emailDw" label={t(tk.sendEmail.ccAddress)}>
            <Form.Control
              type="email"
              value={emailToContractor?.emailDw}
              onChange={e => propertyChange({ emailDw: e.target.value })}
              required
            />
          </FormRow>
          <FormRow controlId="subject" label={t(tk.sendEmail.subject)}>
            <Form.Control
              type="text"
              value={emailToContractor?.subject}
              onChange={e => propertyChange({ subject: e.target.value })}
            />
          </FormRow>
          <FormRow controlId="message" label={t(tk.sendEmail.message)}>
            <div
              className="email-preview-content"
              dangerouslySetInnerHTML={{
                __html: emailToContractor?.message || '',
              }}
            />
          </FormRow>
          <FormRow controlId="files" label={t(tk.sendEmail.fileUpload)}>
            <DragNDropFiles
              setFilesToAdd={setFilesToAdd}
              setFilesToDelete={setFilesToDelete}
              existingFiles={props.existingFiles}
              setMaxFileSizeExceeded={setMaxFileSizeExceeded}
            />
          </FormRow>
        </Form>
        <ValidationAlert show={showErrors} errors={errors} className="m-3" />
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="btn btn-secondary"
          onClick={props.onClose}
          disabled={sending}
        >
          {t(tk.button.close)}
        </Button>
        <Button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={sending}
          isLoading={sending}
        >
          {t(tk.button.send)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
